import React, { useEffect, useMemo, useState } from 'react';
import { FaClipboardList, FaSearch, FaSpinner, FaUserGraduate, FaUserTie, FaHourglassHalf } from 'react-icons/fa';
import { fetchFirstSuccessful, formatShortDate, normalizeStatus, toArray } from '../utils/formExperienceApi';
import '../styles/ManageCurriculums.css';
import '../styles/ManageRanks.css';

// Real backend: GET /crud/evaluations/my -> { evaluations_received: [...], evaluations_given: [...] }
// (see PharmD_System/routers/crud.py get_my_evaluations). Evaluations aren't tied to a
// curriculum in this schema, so everything lands in a single "Unassigned curriculum" bucket.
const MY_EVALUATION_ENDPOINTS = ['/crud/evaluations/my'];

// Direction labels shared with the admin report. `assignment_evaluation` rows only
// exist as form-builder submissions, so they arrive with an explicit direction.
const DIRECTION_LABELS = {
    student_to_preceptor: 'Student → Preceptor',
    preceptor_to_student: 'Preceptor → Student',
    assignment_evaluation: 'Assignment Evaluation'
};

const mapEntry = (entry, fallbackType = 'received') => ({
    id: entry?.evaluationId || entry?._id || entry?.id || `${fallbackType}-${Math.random()}`,
    title: entry?.title || entry?.formTitle || entry?.formName || 'Rotation Evaluation',
    curriculum: entry?.curriculumName || entry?.curriculum || entry?.curriculum_title || 'Unassigned curriculum',
    status: normalizeStatus(entry?.status || (fallbackType === 'pending' ? 'pending' : 'completed')),
    bucket: fallbackType,
    kind: DIRECTION_LABELS[entry?.direction] || 'Evaluation',
    isAssignmentEvaluation: entry?.direction === 'assignment_evaluation',
    evaluator: entry?.evaluatorName || entry?.evaluator || entry?.submittedByName || '—',
    evaluated: entry?.evaluatedName || entry?.evaluatee || entry?.submittedForName || '—',
    submittedAt: entry?.submitted_at || entry?.submittedAt || entry?.createdAt || entry?.updatedAt || null,
    dueDate: entry?.dueDate || entry?.deadline || null
});

const normalizeByCurriculum = (payload) => {
    const data = payload?.data || payload || {};
    const rawBuckets = toArray(data.curricula).length
        ? toArray(data.curricula)
        : toArray(data.byCurriculum);

    if (rawBuckets.length) {
        return rawBuckets.map((bucket) => ({
            curriculum: bucket?.curriculumName || bucket?.curriculum || bucket?.name || 'Unassigned curriculum',
            received: toArray(bucket?.received).map((item) => mapEntry(item, 'received')),
            given: toArray(bucket?.given).map((item) => mapEntry(item, 'given')),
            pending: toArray(bucket?.pending).map((item) => mapEntry(item, 'pending'))
        }));
    }

    const received = toArray(data?.received || data?.evaluationsReceived || data?.evaluations_received).map((item) => mapEntry(item, 'received'));
    const given = toArray(data?.given || data?.evaluationsGiven || data?.evaluations_given).map((item) => mapEntry(item, 'given'));
    const pending = toArray(data?.pending || data?.pendingEvaluations).map((item) => mapEntry(item, 'pending'));
    const grouped = {};

    [...received, ...given, ...pending].forEach((item) => {
        if (!grouped[item.curriculum]) {
            grouped[item.curriculum] = { curriculum: item.curriculum, received: [], given: [], pending: [] };
        }
        grouped[item.curriculum][item.bucket]?.push(item);
    });

    return Object.values(grouped);
};

const bucketPillClass = (bucket) => {
    if (bucket === 'received') return 'is-status-active';
    if (bucket === 'pending') return 'is-status-upcoming';
    return '';
};

const MyEvaluationsPage = ({ userLabel = 'My Evaluations' }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [curriculumFilter, setCurriculumFilter] = useState('all');
    const [bucketFilter, setBucketFilter] = useState('all');

    useEffect(() => {
        const fetchOverview = async () => {
            try {
                setLoading(true);
                const payload = await fetchFirstSuccessful(MY_EVALUATION_ENDPOINTS);
                setRows(normalizeByCurriculum(payload));
            } catch (err) {
                setError(err?.response?.data?.message || 'Unable to load evaluation overview');
            } finally {
                setLoading(false);
            }
        };

        fetchOverview();
    }, []);

    const curriculumOptions = useMemo(() => rows.map((item) => item.curriculum), [rows]);

    const flattened = useMemo(() => {
        const list = [];
        rows.forEach((bucket) => {
            ['received', 'given', 'pending'].forEach((kind) => {
                bucket[kind].forEach((entry) => {
                    list.push({ ...entry, curriculum: bucket.curriculum, bucket: kind });
                });
            });
        });
        return list;
    }, [rows]);

    const filtered = useMemo(() => flattened.filter((item) => {
        if (curriculumFilter !== 'all' && item.curriculum !== curriculumFilter) return false;
        if (bucketFilter === 'assignment') {
            if (!item.isAssignmentEvaluation) return false;
        } else if (bucketFilter !== 'all' && item.bucket !== bucketFilter) {
            return false;
        }
        if (!search.trim()) return true;
        const term = search.toLowerCase();
        return [item.title, item.curriculum, item.evaluator, item.evaluated, item.status, item.kind]
            .some((field) => String(field || '').toLowerCase().includes(term));
    }), [flattened, curriculumFilter, bucketFilter, search]);

    const stats = useMemo(() => ({
        received: flattened.filter((item) => item.bucket === 'received').length,
        given: flattened.filter((item) => item.bucket === 'given').length,
        assignment: flattened.filter((item) => item.isAssignmentEvaluation).length,
        pending: flattened.filter((item) => item.bucket === 'pending').length
    }), [flattened]);

    if (loading) {
        return (
            <div className="mc-page" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
                <FaSpinner className="mc-spin" style={{ fontSize: '3rem', color: '#4a90e2', animation: 'spin 1s linear infinite' }} />
            </div>
        );
    }

    return (
        <div className="mc-page">
            <div className="universal-white-container mc-shell">
                <div className="mc-hero">
                    <div className="mc-hero__copy">
                        <span className="mc-eyebrow">Evaluations</span>
                        <h1>{userLabel}</h1>
                    </div>
                    <div className="mc-hero__stats">
                        <div className="mc-stat"><strong>{stats.received}</strong><span>Received</span></div>
                        <div className="mc-stat"><strong>{stats.given}</strong><span>Given</span></div>
                        <div className="mc-stat"><strong>{stats.assignment}</strong><span>Assignment Evals</span></div>
                        <div className="mc-stat"><strong>{stats.pending}</strong><span>Pending</span></div>
                    </div>
                </div>

                {error ? (
                    <div className="mc-empty-state">
                        <FaClipboardList />
                        <h3>Unable to load evaluations</h3>
                        <p>{error}</p>
                    </div>
                ) : (
                    <>
                        <div className="mc-toolbar">
                            <div className="mc-search-box">
                                <FaSearch />
                                <input
                                    type="text"
                                    placeholder="Search by curriculum, person, status..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <select className="mr-status-filter" value={curriculumFilter} onChange={(e) => setCurriculumFilter(e.target.value)}>
                                <option value="all">All curricula</option>
                                {curriculumOptions.map((curriculum) => (
                                    <option key={curriculum} value={curriculum}>{curriculum}</option>
                                ))}
                            </select>
                            <select className="mr-status-filter" value={bucketFilter} onChange={(e) => setBucketFilter(e.target.value)}>
                                <option value="all">All directions</option>
                                <option value="received">Received</option>
                                <option value="given">Given</option>
                                <option value="assignment">Assignment evaluations</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>

                        {filtered.length === 0 ? (
                            <div className="mc-empty-state">
                                <FaClipboardList />
                                <h3>No evaluations yet</h3>
                                <p>Nothing matches your current filters</p>
                            </div>
                        ) : (
                            <div className="mc-cards-grid">
                                {filtered.map((item) => (
                                    <div key={item.id} className="mc-curriculum-card">
                                        <div className="mc-curriculum-card__top">
                                            <div>
                                                <h4>{item.title}</h4>
                                                <p className="mc-curriculum-card__rank">{item.kind}</p>
                                            </div>
                                            <div className={`mc-status-pill ${bucketPillClass(item.bucket)}`}>
                                                {item.bucket === 'pending' ? <FaHourglassHalf style={{ marginRight: 4 }} /> : null}
                                                {item.bucket}
                                            </div>
                                        </div>
                                        <div className="mc-curriculum-card__meta">
                                            <span><FaClipboardList /> {item.curriculum}</span>
                                            <span><FaUserTie /> Evaluator: {item.evaluator}</span>
                                            <span><FaUserGraduate /> Evaluatee: {item.evaluated}</span>
                                        </div>
                                        <div className="mc-card-metrics">
                                            <div className="mc-card-metric">
                                                <strong style={{ fontSize: '0.95rem' }}>{formatShortDate(item.submittedAt)}</strong>
                                                <small>Submitted</small>
                                            </div>
                                            <div className="mc-card-metric">
                                                <strong style={{ fontSize: '0.95rem' }}>{formatShortDate(item.dueDate)}</strong>
                                                <small>Due</small>
                                            </div>
                                            <div className="mc-card-metric">
                                                <strong style={{ fontSize: '0.95rem', textTransform: 'capitalize' }}>{item.status}</strong>
                                                <small>Status</small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
};

export default MyEvaluationsPage;
